import React from 'react'
import styled from 'styled-components'
import { Card, muiBankTheme } from '@ally/metronome-ui'

interface ComponentProps {
  type: string
  allClosedAccounts?: boolean
}

const StyledCard = styled(Card)<{
  borderColor: string
  allClosedAccounts?: boolean
}>`
  ${({
    borderColor,
    allClosedAccounts,
    theme: { colors, media, space },
  }): string =>
    `
      padding: 0;
      &>div {
        padding: 0;
      }
      ${media.up('lg')} {
        border: 1px solid ${colors['gray-300']};
        padding: 8px;
      }

    &:before {
      background-color: ${borderColor};
      border-color: ${borderColor};
      border-radius: 4px 0 0 4px;
      transition: all .3s ease;
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 7px;
      height: 100%;
    }

    table:not(.bb-tooltip) {
      border: none;
      box-shadow: none;
      table-layout: ${allClosedAccounts ? 'inherit' : 'fixed'};
      thead {
        background: none;
        border-bottom: none;
        th {
          width: 24%;
          font-size: 14px;
          text-transform: capitalize;
          &:hover {
            background-color: initial;
          }
          &:last-child {
            width: ${allClosedAccounts ? '70%' : '21%'};
          }

          button {
            text-align: right;
            padding: 0;
            height: auto;
          }

          &:first-child button {
            text-align: left;
          }
        }
      }
      tbody {
        tr:not([class*=bb-tooltip-name-]) {
          border-bottom: none;
          background: none;
          &.even {
            background-color: initial;
          }
          td {
            button[data-track-name="Close"] {
              top: 30px;
            }
            vertical-align: top;
            button[data-track-name="Close"] {
              right: 0;
            }
          }
          td:first-child {
            white-space: normal;
          }
        }
      }
      tfoot {
        border-top: 1px solid ${colors['slate-2']};
        background-color: initial;
        tr {
          td {
            white-space: nowrap;
          }
          td:first-child {
            white-space: normal;
          }
        }
      }
    }

    @media print {
      @page {
        margin: 50px;
      }
      margin-left: 0;
      margin-right: 0;
      border: 1px solid ${colors['gray-300']};
      border-top: none;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: ${space.md};
      &:before {
        background: none;
        border: none;
      }
      table:not(.bb-tooltip) {
        page-break-inside:auto;
        border-top: 5px solid ${colors['slate-3']};
        thead {
          display: table-row-group;
          page-break-inside:avoid;
          page-break-after:auto;
          tr {
            page-break-inside:avoid;
            page-break-after:auto;
            border-bottom: 1px solid ${colors['slate-3']};
            th {
              background-color: ${colors['page-gray']};
              width 33%;
            }
          }

        }
        tbody {
          a {
            color: ${colors['slate-5']};
          }
          tr {
            page-break-inside:avoid;
            page-break-after:auto;
            :not([class*="bb-tooltip-name-"]) td:first-child {
              padding-right: 0;
            }
            :not([class*="bb-tooltip-name-"]) td:last-child {
              white-space: normal;

              a {
                white-space: normal;
              }
            }
            :not([class*="bb-tooltip-name-"]) td button[data-track-name="Close"] {
              display: none;
            }
          }
        }
        tfoot {
          display: table-row-group;
          page-break-inside:avoid;
          page-break-after:auto;
          border-top: 1px solid  ${colors['slate-2']};
        }
    }
  `};
`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getColor = (type: string): string => {
  switch (type) {
    case 'platinum':
      return muiBankTheme.colors['blue-900' as keyof typeof muiBankTheme.colors]
    case 'everyday':
      return muiBankTheme.colors['blue-300' as keyof typeof muiBankTheme.colors]
    case 'unlimited':
      return muiBankTheme.colors['blue-700' as keyof typeof muiBankTheme.colors]
    default:
      return muiBankTheme.colors[type as keyof typeof muiBankTheme.colors]
  }
}

export const AccountCard: React.FC<ComponentProps> = props => {
  const { type, allClosedAccounts, children } = props
  return (
    <StyledCard
      closeButton={false}
      borderColor={getColor(type.toLowerCase())}
      allClosedAccounts={allClosedAccounts}
    >
      {children}
    </StyledCard>
  )
}
